// src/hooks/useDynamicAnalysis.js
import { useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/api';

export const useDynamicAnalysis = (sha256) => {
  const [dynamicStatus, setDynamicStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  // Fetch current status from database
  const fetchStatus = useCallback(async () => {
    if (!sha256) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${apiService.baseUrl}/dynamic-analysis/${sha256}/status`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch status: ${response.status}`);
      }

      const data = await response.json();
      setDynamicStatus(data);
    } catch (err) {
      console.error('Error fetching dynamic analysis status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sha256]);

  // Check if submission is available
  const checkCanSubmit = useCallback(async () => {
    if (!sha256) return;
    
    try {
      const response = await fetch(
        `${apiService.baseUrl}/dynamic-analysis/${sha256}/can-submit`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to check submission availability: ${response.status}`);
      }

      const data = await response.json();
      setCanSubmit(data.can_submit);
    } catch (err) {
      console.error('Error checking submission availability:', err);
      setCanSubmit(false);
    }
  }, [sha256]);

  // Submit for dynamic analysis
  const submitForAnalysis = useCallback(async () => {
    if (!sha256 || submitting) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      const response = await fetch(
        `${apiService.baseUrl}/dynamic-analysis/${sha256}/submit`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Submission failed');
      }

      const data = await response.json();
      
      // Refresh status
      await fetchStatus();
      
      return { success: true, message: data.message };
    } catch (err) {
      console.error('Error submitting for dynamic analysis:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setSubmitting(false);
    }
  }, [sha256, submitting, fetchStatus]);

  // Fetch results from external service
  const fetchResults = useCallback(async () => {
    if (!sha256 || fetching) return;
    
    try {
      setFetching(true);
      setError(null);
      
      const response = await fetch(
        `${apiService.baseUrl}/dynamic-analysis/${sha256}/fetch`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch results');
      }

      const data = await response.json();
      
      // Refresh status with new results
      await fetchStatus();
      
      // Provide user-friendly message based on external status
      let message = data.message;
      if (data.external_status === 'scheduled') {
        message = 'Analysis is queued and will start shortly.';
      } else if (data.external_status === 'analyzing') {
        message = 'Analysis is still in progress. Please check back later.';
      } else if (data.external_status === 'completed') {
        message = 'Analysis completed! Results are now available.';
      }
      
      return { success: true, data: data.results, message };
    } catch (err) {
      console.error('Error fetching dynamic analysis results:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setFetching(false);
    }
  }, [sha256, fetching, fetchStatus]);

  // Rescan existing analysis
  const rescanAnalysis = useCallback(async () => {
    if (!sha256 || rescanning) return;
    
    try {
      setRescanning(true);
      setError(null);
      
      const response = await fetch(
        `${apiService.baseUrl}/dynamic-analysis/${sha256}/rescan`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Rescan failed');
      }

      const data = await response.json();
      
      // Refresh status
      await fetchStatus();
      
      return { success: true, message: data.message };
    } catch (err) {
      console.error('Error rescanning dynamic analysis:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setRescanning(false);
    }
  }, [sha256, rescanning, fetchStatus]);

  // Initial load
  useEffect(() => {
    if (sha256) {
      fetchStatus();
      checkCanSubmit();
    }
  }, [sha256, fetchStatus, checkCanSubmit]);

  return {
    dynamicStatus,
    loading,
    error,
    canSubmit,
    submitting,
    fetching,
    rescanning,
    submitForAnalysis,
    fetchResults,
    rescanAnalysis,
    refetch: fetchStatus
  };
};